import { useEffect } from 'react';


export interface KeyboardShortcuts {
    onToolJump?: (index: number) => void; // Cmd/Ctrl + 1-9
    onWorkspaceSelector?: () => void; // Cmd/Ctrl + Shift + W
    onCommandPalette?: () => void; // Cmd/Ctrl + K
    onSidebarFocus?: () => void; // Arrow keys when sidebar focused
    isEnabled?: boolean;
}

/**
 * Global keyboard navigation hook for workspace shortcuts
 * 
 * Shortcuts:
 * - Cmd/Ctrl + 1-9: Jump to tool by sidebar position
 * - Cmd/Ctrl + Shift + W: Open workspace selector
 * - Arrow keys: Navigate sidebar when focused (handled by component)
 * - Cmd/Ctrl + Z: Undo (scoped to active tool - handled by feature)
 */
export const useKeyboardNavigation = ({
    onToolJump,
    onWorkspaceSelector,
    onCommandPalette,
    isEnabled = true,
}: KeyboardShortcuts) => {
    useEffect(() => {
        if (!isEnabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

            // Cmd/Ctrl + Space: Open workspace selector
            if (cmdOrCtrl && e.key === ' ') {
                e.preventDefault();
                onWorkspaceSelector?.();
                return;
            }

            // Cmd/Ctrl + K: Open command palette
            if (cmdOrCtrl && e.key === 'k') {
                e.preventDefault();
                onCommandPalette?.();
                return;
            }

            // Cmd/Ctrl + 1-9: Jump to tool
            if (cmdOrCtrl && !e.shiftKey && !e.altKey) {
                const key = e.key;
                const num = Number.parseInt(key, 10);
                if (num >= 1 && num <= 9) {
                    e.preventDefault();
                    onToolJump?.(num - 1); // 0-indexed
                    return;
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onToolJump, onWorkspaceSelector, onCommandPalette, isEnabled]);
};

/**
 * Helper to get keyboard shortcut display text
 */
export const getShortcutKey = (key: string): string => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? `⌘${key}` : `Ctrl+${key}`;
};

/**
 * Helper to get all shortcuts as documentation
 */
export const KEYBOARD_SHORTCUTS = {
    toolJump: {
        keys: ['Cmd/Ctrl + 1-9'],
        description: 'Jump to tool by sidebar position'
    },
    workspaceSelector: {
        keys: ['Cmd/Ctrl + Space'],
        description: 'Open workspace selector'
    },
    commandPalette: {
        keys: ['Cmd/Ctrl + K'],
        description: 'Open command palette'
    },
    sidebarNavigation: {
        keys: ['Arrow Keys'],
        description: 'Navigate sidebar when focused'
    },
    undo: {
        keys: ['Cmd/Ctrl + Z'],
        description: 'Undo (scoped to active tool)'
    },
    redo: {
        keys: ['Cmd/Ctrl + Shift + Z'],
        description: 'Redo (scoped to active tool)'
    }
};














